import { HangoutState } from '../../state/HangoutState.js';
import { storageManager } from '../../../../core/storage/index.js';
import { getTimeDefinitionService } from '../../../../core/services/TimeDefinitionService.js';
import {
    readHistoricalSettingsScene,
    resolveSettingsBackdrop
} from './settings-backdrop.js';

export { mapTimeOfDayToValue } from './settings-backdrop.js';

/**
 * Map select value to time of day string
 * @param {string} value - '0.1', '0.2', or '0.3'
 * @returns {string} - 'day', 'sunset', or 'night'
 */
export const mapValueToTimeOfDay = (value) => {
    const map = { '0.1': 'day', '0.2': 'sunset', '0.3': 'night' };
    return map[value] || 'night';
};

/**
 * Reconstruct current state from message history and session data
 * Used to populate the settings modal with current values
 * @returns {Promise<{
 *   timeOfDay: string,
 *   backgroundId: string|null,
 *   backgroundUrl: string|null,
 *   customScenarioDescription: string|null,
 *   isScenarioDescriptionCustom: boolean,
 *   characters: Array,
 *   goals: Object,
 *   customGoals: Object<string, boolean>
 * }>}
 */
export async function getCurrentStateFromMessages() {
    const hangoutState = HangoutState.getInstance();
    const messages = hangoutState.getMessages();
    const currentSession = hangoutState.getCurrentSession();
    const hangoutConfig = await storageManager.getItem('currentHangoutConfig');
    const timeService = getTimeDefinitionService();

    let currentTimeOfDay = hangoutState.getCurrentTimeSlot() || 'night';
    if (window.timeProgressionService) {
        currentTimeOfDay = timeService.hourToTimeOfDay(window.timeProgressionService.getCurrentTime());
    } else {
        const timeSlot = hangoutState.getCurrentTimeSlot() || 'night';
        currentTimeOfDay = timeService.getTimeOfDayFromNumericSlot(timeSlot) || timeSlot;
    }

    let state = {
        timeOfDay: currentTimeOfDay,
        backgroundId: null,
        backgroundUrl: null,
        customScenarioDescription: null,
        isScenarioDescriptionCustom: false,
        characters: [],
        goals: {},
        customGoals: {}
    };

    const inMemoryCustomGoals = hangoutState.state?.customGoals || {};
    if (hangoutConfig?.customGoals && typeof hangoutConfig.customGoals === 'object') {
        Object.entries(hangoutConfig.customGoals).forEach(([charId, isCustom]) => {
            if (isCustom) state.customGoals[String(charId)] = true;
        });
    }
    if (inMemoryCustomGoals && typeof inMemoryCustomGoals === 'object') {
        Object.entries(inMemoryCustomGoals).forEach(([charId, isCustom]) => {
            if (isCustom) state.customGoals[String(charId)] = true;
        });
    }

    const storedScenarioDescription = hangoutConfig?.customGoal || hangoutConfig?.scenarioDescription || null;
    if (hangoutConfig?.isScenarioDescriptionCustom === true && storedScenarioDescription) {
        state.customScenarioDescription = storedScenarioDescription;
        state.isScenarioDescriptionCustom = true;
    }

    // Fallback to HangoutState
    if (!state.customScenarioDescription) {
        const liveCustomDescription = hangoutState.getCustomScenarioDescription();
        const hasUserMessages = Array.isArray(messages) && messages.some(msg => msg?.role === 'user');
        if (liveCustomDescription && hasUserMessages) {
            state.customScenarioDescription = liveCustomDescription;
            state.isScenarioDescriptionCustom = true;
        }
    }

    // Check messages for scenario description
    if (!state.customScenarioDescription && messages.length > 0) {
        for (let i = messages.length - 1; i >= 0; i--) {
            const msg = messages[i];
            if (msg.metadata?.customScenarioDescription) {
                state.customScenarioDescription = msg.metadata.customScenarioDescription;
                state.isScenarioDescriptionCustom = true;
                break;
            }
        }
    }

    // Final fallback to scenario
    if (!state.customScenarioDescription) {
        const selectedScenario = hangoutState.getSelectedScenario();
        state.customScenarioDescription = selectedScenario?.description || selectedScenario?.descr;
    }

    if (messages.length === 0 && currentSession?.scenarioDescription) {
        state.customScenarioDescription = currentSession.scenarioDescription;
    }

    if (messages.length > 0) {
        for (const msg of messages) {
            const stateDiff = msg.stateDiff || {};
            if (stateDiff._noChanges) continue;
            const description = msg.metadata?.customScenarioDescription;
            if (!description) continue;
            state.customScenarioDescription = description;
            state.isScenarioDescriptionCustom = true;
        }
    }

    try {
        if (hangoutConfig?.allCharacters) {
            state.characters = hangoutConfig.allCharacters;
            hangoutConfig.allCharacters.forEach(char => {
                if (char.goal) {
                    state.goals[char.id] = char.goal;
                }
            });
        } else if (hangoutConfig?.allCharacters?.[0]?.id) {
            const allCharacters = hangoutState.getCharacters();
            const primaryCharacterId = hangoutConfig.allCharacters[0].id;
            const singleCharacter = allCharacters.find(c => c.id.toString() === primaryCharacterId.toString());
            if (singleCharacter) {
                const configCharacter = hangoutConfig.allCharacters[0];
                state.characters = [{
                    id: primaryCharacterId,
                    name: singleCharacter.name,
                    outfitId: String(configCharacter?.outfitId || 0),
                    goal: hangoutConfig.goal
                }];
            }
        }

        // Get characters from session if not found in config
        if (state.characters.length === 0 && currentSession) {
            if (currentSession.allCharacters?.length > 0) {
                state.characters = currentSession.allCharacters;
            } else if (currentSession.characterId) {
                const allCharacters = hangoutState.getCharacters();
                const sessionCharacter = allCharacters.find(c => c.id.toString() === currentSession.characterId);
                if (sessionCharacter) {
                    state.characters = [{
                        id: currentSession.characterId,
                        name: sessionCharacter.name,
                        outfitId: String(currentSession.outfitId || hangoutConfig?.allCharacters?.[0]?.outfitId || 0),
                        goal: currentSession.goal || hangoutConfig?.goal || null
                    }];
                }
            }
        }

        // Get goals from config
        if (hangoutConfig?.goal && state.characters.length > 0) {
            state.goals[state.characters[0].id] = hangoutConfig.goal;
        }

        // Restore goals from message metadata
        if (Object.keys(state.goals).length === 0 && messages.length > 0) {
            for (let i = messages.length - 1; i >= 0; i--) {
                const msg = messages[i];
                if (msg.metadata?.customGoals) {
                    Object.entries(msg.metadata.customGoals).forEach(([charId, goal]) => {
                        if (goal) {
                            state.goals[charId] = goal;
                            state.customGoals[String(charId)] = true;
                        }
                    });
                    break;
                } else if (msg.metadata?.currentGoal && state.characters.length > 0) {
                    const firstCharId = state.characters[0].id;
                    if (msg.metadata.currentGoal) {
                        state.goals[firstCharId] = msg.metadata.currentGoal;
                        state.customGoals[String(firstCharId)] = true;
                    }
                    break;
                }
            }
        }

        // Check for character goals in message history
        if (messages.length > 0) {
            for (let i = messages.length - 1; i >= 0; i--) {
                const msg = messages[i];
                if (msg.metadata?.goalChange) {
                    const { characterId, newGoal } = msg.metadata.goalChange;
                    if (newGoal && characterId && !state.goals[characterId]) {
                        state.goals[characterId] = newGoal;
                        state.customGoals[String(characterId)] = true;
                        const character = state.characters.find(c => String(c.id) === String(characterId));
                        if (character) character.goal = newGoal;
                    }
                }
            }
        }

        // Get goals from scenario for new conversations
        if (Object.keys(state.goals).length === 0 && messages.length === 0) {
            const selectedScenario = hangoutState.getSelectedScenario();
            if (selectedScenario?.goal && state.characters.length > 0) {
                state.goals[state.characters[0].id] = selectedScenario.goal;
                state.characters[0].goal = selectedScenario.goal;
            }
        }

    } catch (error) {
        console.error('Error getting character state:', error);
    }

    return state;
}

export const refreshBackgroundSearchTriggers = () => {
    window.searchDropdownRegistry?.['generate-bg-settings']?.refresh?.();
    window.searchDropdownRegistry?.['time-of-day-settings']?.refresh?.();
};

const readSettingsClockHour = () => {
    if (typeof window === 'undefined' || !window.timeProgressionService?.getCurrentTime) return null;
    return window.timeProgressionService.getCurrentTime();
};

export async function resolveSettingsLocationAndTime() {
    const hangoutState = HangoutState.getInstance();
    const hangoutConfig = await storageManager.getItem('currentHangoutConfig') || {};
    const messages = hangoutState.getVisibleMessages?.() || hangoutState.getMessages?.() || [];
    const tipPath = hangoutState.getBranchTipPath?.()
        || (messages.length ? messages[messages.length - 1]?.path : null);
    const historicalScene = readHistoricalSettingsScene(messages, tipPath);
    const scenario = hangoutState.getSelectedScenario?.() || null;
    const clockHour = readSettingsClockHour();
    const resolved = resolveSettingsBackdrop({
        historicalScene,
        config: hangoutConfig,
        scenario,
        clockHour,
        backgroundUrl: hangoutConfig?.backgroundUrl || null
    });
    if (resolved.backgroundId) return resolved;

    let selectedBackgroundUrl = null;
    try {
        selectedBackgroundUrl = await hangoutState.getSelectedBackground();
    } catch {
        selectedBackgroundUrl = null;
    }
    return resolveSettingsBackdrop({
        historicalScene,
        config: hangoutConfig,
        scenario,
        clockHour,
        backgroundUrl: selectedBackgroundUrl || hangoutConfig?.backgroundUrl || null
    });
}

export const applySettingsLocationAndTimeToSelects = (backgroundSelect, timeOfDaySelect, { backgroundId, timeValue } = {}) => {
    if (timeOfDaySelect && timeValue) {
        timeOfDaySelect.value = timeValue;
    }
    if (backgroundSelect && backgroundId) {
        const hasOption = Array.from(backgroundSelect.options).some(
            (opt) => String(opt.value) === String(backgroundId)
        );
        if (hasOption) backgroundSelect.value = String(backgroundId);
    }
    backgroundSelect?.dispatchEvent(new Event('change'));
    timeOfDaySelect?.dispatchEvent(new Event('change'));
    refreshBackgroundSearchTriggers();
};
